// Cue 庫專案：機關時間（延遲 / 走幾秒）放開就存時用的純函式。
// 只動時間，不動位置值：還沒存的位置值（草稿）不會被寫進去，也不會被清掉。

import type { Look, LookMechRow } from '../cueLibraryApi';
import type { DraftMech, LookDraft } from '../lookDraft';
import type { BulkPositionItem } from './group';

/**
 * 把一批時間改動套在「已經存著的畫面」上，給「只改這首」另存新 cue 用。
 * 已經有這一列 → 位置值照舊、只換時間（沿用的列變成自己的）；沒有 → 用 item 帶的位置值（呼叫的人放存著的值 / 預設位置）。
 * value = null 的 item 當作刪掉這一列。
 */
export function applyTimingItems(look: Look, items: readonly BulkPositionItem[]): Look {
  const byId = new Map(items.map(i => [i.mechanismId, i]));
  const seen = new Set<string>();
  const mechanisms: LookMechRow[] = [];
  for (const r of look.mechanisms) {
    const it = byId.get(r.mechanismId);
    seen.add(r.mechanismId);
    if (!it) { mechanisms.push(r); continue; }
    if (it.value === null) continue;
    mechanisms.push({
      mechanismId: r.mechanismId, value: r.value, carried: false,
      delaySec: it.delaySec !== undefined ? it.delaySec : (r.carried ? null : r.delaySec),
      durationSec: it.durationSec !== undefined ? it.durationSec : (r.carried ? null : r.durationSec),
    });
  }
  for (const it of items) {
    if (seen.has(it.mechanismId) || it.value === null) continue;
    mechanisms.push({ mechanismId: it.mechanismId, value: it.value, delaySec: it.delaySec ?? null, durationSec: it.durationSec ?? null, carried: false });
  }
  return { objects: look.objects, mechanisms };
}

/**
 * 時間已經存進 Cue 庫之後，同步「還沒存的改動」裡同一個機關的時間（位置值不動）。
 * 不然之後按「覆蓋」會把剛存好的時間又蓋回舊的。草稿裡沒有這個機關 → 不動。
 */
export function syncDraftTimings(d: LookDraft, items: readonly BulkPositionItem[]): LookDraft {
  let changed = false;
  const mechanisms: Record<string, DraftMech | null> = { ...d.mechanisms };
  for (const it of items) {
    const cur = mechanisms[it.mechanismId];
    if (!cur) continue;
    // value = null：存著的那一列被拿掉了 → 時間也沒有了
    const gone = it.value === null;
    const next = {
      ...cur,
      delaySec: gone ? null : it.delaySec !== undefined ? it.delaySec : cur.delaySec,
      durationSec: gone ? null : it.durationSec !== undefined ? it.durationSec : cur.durationSec,
    };
    if (next.delaySec !== cur.delaySec || next.durationSec !== cur.durationSec) { mechanisms[it.mechanismId] = next; changed = true; }
  }
  return changed ? { ...d, mechanisms } : d;
}
